import {
  CircularProgress,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { FormEvent, RefObject, useRef, useState } from "react";
import { SendHorizontal, Paperclip } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  AttachButton,
  Container,
  InputWrapper,
  SendButton,
} from "@components/ChatAgent/components/ChatAgentMessageInput/style";

const UPLOAD_ACCEPT = ".csv,.xlsx,.xls";

interface ChatAgentMessageInputProps {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
  isLoading?: boolean;
  inputRef: RefObject<HTMLInputElement>;
  showAttachButton?: boolean;
  onUploadFile?: (file: File) => void;
  showUploadButton?: boolean;
  isUploading?: boolean;
}

const ChatAgentMessageInput = ({
  onSendMessage,
  disabled,
  isLoading,
  inputRef,
  showAttachButton = false,
  onUploadFile,
  showUploadButton = false,
  isUploading = false,
}: ChatAgentMessageInputProps) => {
  const [message, setMessage] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation("agent_chat");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onUploadFile) onUploadFile(file);
    e.target.value = "";
  };

  const sendMessage = (e?: FormEvent<HTMLFormElement>) => {
    if (e) e.preventDefault();
    if (!message) return;
    setMessage("");
    onSendMessage(message);
    if (inputRef.current) inputRef.current.focus();
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && !e.altKey && !e.shiftKey) {
      sendMessage();
      e.preventDefault();
    }
  };

  return (
    <Container>
      {showAttachButton && (
        <AttachButton disabled={disabled}>
          <Paperclip size={20} />
        </AttachButton>
      )}
      {showUploadButton && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept={UPLOAD_ACCEPT}
            hidden={true}
            onChange={handleFileChange}
          />
          <Tooltip title={t("uploadFileTooltip") || ""} arrow={true}>
            <span>
              <AttachButton
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled || isUploading}
                aria-label={t("uploadFileTooltip") || ""}
              >
                {isUploading ? (
                  <CircularProgress size={20} />
                ) : (
                  <Paperclip size={20} />
                )}
              </AttachButton>
            </span>
          </Tooltip>
        </>
      )}
      <InputWrapper>
        <form onSubmit={sendMessage} style={{ width: "100%" }}>
          <TextField
            autoFocus={true}
            disabled={disabled}
            inputRef={inputRef}
            variant={"standard"}
            placeholder={t("input_ph") || ""}
            size={"small"}
            value={message}
            sx={{
              width: "100%",
              "& .MuiInputBase-root": {
                color: "#000000",
                fontSize: "14px",
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#888888",
                opacity: 1,
              },
            }}
            multiline={true}
            maxRows={2}
            onKeyDown={(event) => handleInputKeyDown(event)}
            onChange={(event) => setMessage(event.target.value)}
            InputProps={{
              disableUnderline: true,
            }}
          />
        </form>
      </InputWrapper>
      <SendButton
        onClick={() => sendMessage()}
        disabled={!message || disabled || isLoading}
        variant="contained"
        endIcon={
          isMobile ? undefined : isLoading ? (
            <CircularProgress size={16} sx={{ color: "#ffffff" }} />
          ) : (
            <SendHorizontal size={16} />
          )
        }
      >
        {isMobile ? (
          isLoading ? (
            <CircularProgress size={16} sx={{ color: "#ffffff" }} />
          ) : (
            <SendHorizontal size={16} />
          )
        ) : (
          t("send_btn")
        )}
      </SendButton>
    </Container>
  );
};

export default ChatAgentMessageInput;
